import * as React from 'react';
import {Redirect, Route, Switch} from 'react-router-dom';

import {setBodyId} from '../../../dom';

import {LandingPagesListPage} from '../../landing-pages/landing-pages-list-page';
import {LandingPagesSettingsPage} from '../../landing-pages/landing-pages-settings-page';
import {LandingPagePage} from '../../landing-pages/views/landing-page-page';
import {SmsCampaignsFeatureGate} from '../../limits/feature-gates/sms-campaigns-feature-gate';
import {DomainsPageStandalone} from '../../setup/domains/domains-page-standalone';
import {SmsSettingsPageStandalone} from '../../setup/text-messaging/sms-settings-page-standalone';

import {AudiencePage} from '../audiences/audience-page';
import {AudienceListPage} from '../audiences/audiences-list-page';
import {AiDesignPage} from '../broadcasts/ai-design/ai-design-page';
import {BroadcastPageWrapper} from '../broadcasts/broadcast-page-wrapper';
import {BroadcastsListPage} from '../broadcasts/broadcasts-list-page';
import {DripSequencePage} from '../drip-sequences/drip-sequence-page';
import {DripSequencesListPage} from '../drip-sequences/drip-sequences-list-page';
import {DripSequenceEditionPageWrapper} from '../drip-sequences/editions/drip-sequence-edition-page-wrapper';
import {EditionPageWrapper} from '../editions/edition-page-wrapper';
import {DEFAULT_EMAIL_MARKETING_REDIRECT_PATH} from '../email-marketing-root';
import {NewsletterPage} from '../newsletters/newsletter-page';
import {NewslettersListPage} from '../newsletters/newsletters-list-page';
import {PreferenceCenterPage} from '../preference-center/preference-center-page';
import {SenderAddressesPageStandalone} from '../sender-addresses/sender-addresses-page-standalone';
import {BroadcastPageWrapper as SMSBroadcastPageWrapper} from '../sms/broadcasts/broadcast-page-wrapper';
import {BroadcastsListPage as SMSBroadcastListPage} from '../sms/broadcasts/broadcasts-list-page';
import {DripSequencePage as SMSDripSequencePage} from '../sms/drip-sequences/drip-sequence-page/drip-sequence-page';
import {DripSequencesListPage as SMSDripSequencesListPage} from '../sms/drip-sequences/drip-sequences-list-page';
import {DripSequenceEditionPage as SMSDripSequenceEditionPage} from '../sms/drip-sequences/editions/drip-sequence-edition-page';
import {Summary} from '../summary/summary';
import {TemplatePageWrapper} from '../templates/template-page-wrapper';
import {TemplatesListPage} from '../templates/templates-list-page';

import {ListPageRoute} from './list-page-route';
import {PageRoute} from './page-route';
import {RestrictedListPageRoute} from './restricted-list-page-route';
import {RestrictedPageRoute} from './restricted-page-route';
import {RestrictedRoute} from './restricted-route';

const BroadcastAiDesignPage = (props: any) => <AiDesignPage {...props} targetType='broadcasts' />;
const TemplateAiDesignPage = (props: any) => <AiDesignPage {...props} targetType='templates' />;
const NewsletterAiDesignPage = (props: any) => <AiDesignPage {...props} targetType='newsletters' />;
const DripSequenceAiDesignPage = (props: any) => (
    <AiDesignPage {...props} targetType='drip-sequences' />
);

const DRIP_SEQUENCES_VIEW_SEARCH = 'DRIP_SEQUENCES_VIEW_SEARCH';
const NEWSLETTERS_VIEW_SEARCH = 'NEWSLETTERS_VIEW_SEARCH';
const NEWSLETTER_PAGE_VIEW_SEARCH = 'NEWSLETTER_PAGE_VIEW_SEARCH';
const BROADCAST_VIEW_SEARCH = 'BROADCAST_VIEW_SEARCH';
const AUDIENCES_VIEW_SEARCH = 'AUDIENCES_VIEW_SEARCH';
const TEMPLATES_VIEW_SEARCH = 'TEMPLATE_VIEW_SEARCH';
const LANDING_PAGES_VIEW_SEARCH = 'LANDING_PAGES_VIEW_SEARCH';
const SMS_BROADCAST_VIEW_SEARCH = 'SMS_BROADCAST_VIEW_SEARCH';
const SMS_DRIP_SEQUENCES_VIEW_SEARCH = 'SMS_DRIP_SEQUENCES_VIEW_SEARCH';

type Props = {
    hasFullAccessToCampaigns: boolean;
    canAccessSetup?: boolean;
};

/**
 * This file is title 'shared-routes' because these routes our shared between the base Nutshell app
 * and the Nuthshell app embedded in WebFX (https://customer-site.squirrel.house/)
 */

/**
 * Routes for the summary page, newsletters, broadcasts, and drip sequences all wrapped in a <Switch>. These will not
 * work properly if rendered inside another <Switch> block, but will work as expected if placed as a sibling.
 *
 * @returns {JSX.Element}
 * @constructor
 */
export const EmailMarketingRoutes = (props: Props) => {
    const rootPath = '/marketing';
    const emailRootPath = '/marketing/email';
    const canAccessSetup =
        typeof props.canAccessSetup === 'undefined' ? true : props.canAccessSetup;

    return (
        <Switch>
            {/** This page has been moved to /email-marketing/dashboard */}
            <RestrictedRoute
                path={`${rootPath}/summary`}
                exact={true}
                render={(renderProps) => {
                    return <Summary {...renderProps} />;
                }}
                hasAccess={false}
                redirectPath='/marketing/dashboard'
            />
            {/* In the old world, we use the depricated /email-marketing/dashboard to
            shows the old summary page if you are marketing-limited. That summary page is not
            accessible if you are a full marketing access user. In the new world, since we
            are eliminating /email-marketing, I have renamed this to /marketing/summary. */}
            <RestrictedRoute
                path='/marketing/email/summary'
                exact={true}
                render={(renderProps) => {
                    return <Summary {...renderProps} />;
                }}
                hasAccess={!props.hasFullAccessToCampaigns}
                redirectPath='/marketing/dashboard'
            />
            <RestrictedListPageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/newsletters`}
                localStorageKey={NEWSLETTERS_VIEW_SEARCH}
                defaultQuery='?view=board&sort=-modifiedTime'
                component={NewslettersListPage}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <Redirect
                exact={true}
                from={`${emailRootPath}/newsletters/:newsletterId`}
                to={`${emailRootPath}/newsletters/:newsletterId/editions`}
            />
            <RestrictedListPageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/newsletters/:newsletterId/:tab`}
                localStorageKey={NEWSLETTER_PAGE_VIEW_SEARCH}
                defaultQuery='?view=board&sort=-modifiedTime'
                component={NewsletterPage}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <Redirect
                exact={true}
                from={`${emailRootPath}/newsletters/:newsletterId/editions/:editionId`}
                to={`${emailRootPath}/newsletters/:newsletterId/editions/:editionId/editor`}
            />
            <RestrictedPageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/newsletters/:newsletterId/editions/:editionId/:tab`}
                reportKey='editionEngagement'
                initialFilterKey='sentEditions'
                idParam='editionId'
                component={EditionPageWrapper}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <ListPageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/broadcasts`}
                localStorageKey={BROADCAST_VIEW_SEARCH}
                defaultQuery='?view=board&sort=-modifiedTime'
                component={BroadcastsListPage}
            />
            <Redirect
                exact={true}
                from={`${emailRootPath}/broadcasts/archived`}
                to={`${emailRootPath}/broadcasts`}
            />
            <Redirect
                exact={true}
                from={`${emailRootPath}/broadcasts/:broadcastId/editions/:editionId`}
                to={`${emailRootPath}/broadcasts/:broadcastId/editions/:editionId/editor`}
            />
            <PageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/broadcasts/:broadcastId/editions/:editionId/:tab`}
                reportKey='editionEngagement'
                initialFilterKey='sentEditions'
                idParam='editionId'
                component={BroadcastPageWrapper}
            />
            <Route
                exact={true}
                path={`${emailRootPath}/broadcasts/:campaignId/design/:editionId`}
                component={BroadcastAiDesignPage}
            />
            <Route
                exact={true}
                path={`${emailRootPath}/templates/:campaignId/design/:editionId`}
                component={TemplateAiDesignPage}
            />
            <RestrictedRoute
                exact={true}
                path={`${emailRootPath}/newsletters/:campaignId/design/:editionId`}
                render={(routeProps: any) => <NewsletterAiDesignPage {...routeProps} />}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <RestrictedRoute
                exact={true}
                path={`${emailRootPath}/drip-sequences/:campaignId/design/:editionId`}
                render={(routeProps: any) => <DripSequenceAiDesignPage {...routeProps} />}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <Redirect
                exact={true}
                from={`${emailRootPath}/templates/:templateId/editions/:editionId`}
                to={`${emailRootPath}/templates/:templateId/editions/:editionId/editor`}
            />
            <PageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/templates/:templateId/editions/:editionId/:tab`}
                idParam='editionId'
                component={TemplatePageWrapper}
            />
            <RestrictedListPageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/drip-sequences`}
                localStorageKey={DRIP_SEQUENCES_VIEW_SEARCH}
                defaultQuery='?view=board&sort=-modifiedTime'
                component={DripSequencesListPage}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <Redirect
                exact={true}
                from={`${emailRootPath}/drip-sequences/:dripSequenceId`}
                to={`${emailRootPath}/drip-sequences/:dripSequenceId/messages`}
            />
            <RestrictedPageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/drip-sequences/:dripSequenceId/:tab`}
                reportKey='dripEditionEngagement'
                initialFilterKey='sentDripSequences'
                idParam='dripSequenceId'
                component={DripSequencePage}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <Redirect
                exact={true}
                from={`${emailRootPath}/drip-sequences/:dripSequenceId/editions/:dripSequenceEditionId`}
                to={`${emailRootPath}/drip-sequences/:dripSequenceId/editions/:dripSequenceEditionId/editor`}
            />
            <RestrictedPageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/drip-sequences/:dripSequenceId/editions/:dripSequenceEditionId/:tab`}
                reportKey='dripEditionEngagement'
                initialFilterKey='sentEditions'
                idParam='dripSequenceEditionId'
                component={DripSequenceEditionPageWrapper}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <RestrictedRoute
                path={`${rootPath}/domains`}
                exact={true}
                render={() => {
                    return <DomainsPageStandalone />;
                }}
                hasAccess={props.hasFullAccessToCampaigns && canAccessSetup}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <RestrictedRoute
                path={`${rootPath}/sender-address`}
                exact={true}
                render={(renderProps) => {
                    setBodyId('list-sender-address');
                    const body = document.querySelector('body');

                    if (body) {
                        body.className = 'manage page settings list';
                    }

                    return <SenderAddressesPageStandalone search={renderProps.location.search} />;
                }}
                hasAccess={props.hasFullAccessToCampaigns && canAccessSetup}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <RestrictedListPageRoute
                // @ts-expect-error
                exact={true}
                path={'/marketing/audiences'}
                localStorageKey={AUDIENCES_VIEW_SEARCH}
                defaultQuery='?sort=-modifiedTime'
                component={AudienceListPage}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <RestrictedPageRoute
                // @ts-expect-error
                exact={true}
                path='/marketing/audiences/:audienceId/:tab'
                reportKey='audienceMembership'
                initialFilterKey='audiences'
                idParam='audienceId'
                component={AudiencePage}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <Redirect exact={true} from='/email-marketing/audiences' to='/marketing/audiences' />
            <Redirect
                exact={true}
                from='/email-marketing/audiences/:audienceId'
                to='/marketing/audiences/:audienceId'
            />
            <Redirect
                exact={true}
                from='/email-marketing/audiences/:audienceId/:tab'
                to='/marketing/audiences/:audienceId/:tab'
            />
            <Redirect
                exact={true}
                from={'/marketing/email/audiences'}
                to={'/marketing/audiences'}
            />
            <Redirect
                exact={true}
                from={'/marketing/email/audiences/:audienceId'}
                to={'/marketing/audiences/:audienceId'}
            />
            <Redirect
                exact={true}
                from='/marketing/email/audiences/:audienceId/:tab'
                to='/marketing/audiences/:audienceId/:tab'
            />
            <Redirect
                exact={true}
                from='/marketing/audiences/:audienceId'
                to='/marketing/audiences/:audienceId/engagement'
            />
            <RestrictedListPageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/templates`}
                localStorageKey={TEMPLATES_VIEW_SEARCH}
                defaultQuery='?view=board&sort=-modifiedTime'
                component={TemplatesListPage}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <RestrictedPageRoute
                // @ts-expect-error
                exact={true}
                path={`${emailRootPath}/preference-center`}
                component={PreferenceCenterPage}
                hasAccess={
                    canAccessSetup &&
                    props.hasFullAccessToCampaigns &&
                    NutClientConfig.hasPreferenceCenterFF
                }
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <RestrictedListPageRoute
                // @ts-expect-error
                exact={true}
                path={`${rootPath}/landing-pages`}
                localStorageKey={LANDING_PAGES_VIEW_SEARCH}
                defaultQuery='?view=board&sort=-modifiedTime'
                component={LandingPagesListPage}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <Redirect
                exact={true}
                from={`${rootPath}/landing-pages/:pageId`}
                to={`${rootPath}/landing-pages/:pageId/main`}
            />
            <RestrictedPageRoute
                // @ts-expect-error
                exact={true}
                path={`${rootPath}/landing-pages/:pageId/:tab`}
                reportKey='formEngagement'
                initialFilterKey='pages'
                idParam='pageId'
                component={LandingPagePage}
                hasAccess={props.hasFullAccessToCampaigns}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
            <RestrictedPageRoute
                path='/marketing/landing-pages-settings'
                // @ts-expect-error
                exact={true}
                component={LandingPagesSettingsPage}
                hasAccess={canAccessSetup}
                redirectPath={DEFAULT_EMAIL_MARKETING_REDIRECT_PATH}
            />
        </Switch>
    );
};

type SmsMarketingRoutesProps = {
    hasFullAccessToCampaigns: boolean;
    canAccessSetup?: boolean;
    redirectPath: string;
};

/**
 * Routes for the SMS broadcasts and drip sequences all wrapped in a <Switch>.
 *
 * @returns {JSX.Element}
 * @constructor
 */
export const SmsMarketingRoutes = (props: SmsMarketingRoutesProps) => {
    const canAccessSetup =
        typeof props.canAccessSetup === 'undefined' ? true : props.canAccessSetup;

    return (
        <Switch>
            <ListPageRoute
                // @ts-expect-error
                exact={true}
                path='/marketing/sms/broadcasts'
                localStorageKey={SMS_BROADCAST_VIEW_SEARCH}
                defaultQuery='?view=board&sort=-modifiedTime'
                component={
                    NutClientConfig.hasSmsCampaignsFeature
                        ? SMSBroadcastListPage
                        : SmsCampaignsFeatureGate
                }
            />
            <Redirect
                exact={true}
                from='/marketing/sms/broadcasts/:broadcastId/editions/:editionId'
                to='/marketing/sms/broadcasts/:broadcastId/editions/:editionId/details'
            />
            <PageRoute
                // @ts-expect-error
                exact={true}
                path='/marketing/sms/broadcasts/:broadcastId/editions/:editionId/:tab'
                reportKey='smsEditionEngagement'
                initialFilterKey='sentSmsEditions'
                idParam='editionId'
                component={
                    NutClientConfig.hasSmsCampaignsFeature
                        ? SMSBroadcastPageWrapper
                        : SmsCampaignsFeatureGate
                }
            />
            <ListPageRoute
                // @ts-expect-error
                exact={true}
                path='/marketing/sms/drip-sequences'
                localStorageKey={SMS_DRIP_SEQUENCES_VIEW_SEARCH}
                defaultQuery='?view=board&sort=-modifiedTime'
                component={
                    NutClientConfig.hasSmsCampaignsFeature
                        ? SMSDripSequencesListPage
                        : SmsCampaignsFeatureGate
                }
            />
            <Redirect
                exact={true}
                from='/marketing/sms/drip-sequences/:dripSequenceId/editions/:dripSequenceEditionId'
                to='/marketing/sms/drip-sequences/:dripSequenceId/editions/:dripSequenceEditionId/details'
            />
            <PageRoute
                // @ts-expect-error
                exact={true}
                path='/marketing/sms/drip-sequences/:dripSequenceId/editions/:dripSequenceEditionId/:tab'
                reportKey='smsDripEditionEngagement'
                initialFilterKey='sentSmsEditions'
                idParam='dripSequenceEditionId'
                component={
                    NutClientConfig.hasSmsCampaignsFeature
                        ? SMSDripSequenceEditionPage
                        : SmsCampaignsFeatureGate
                }
            />
            <Redirect
                exact={true}
                from='/marketing/sms/drip-sequences/:dripSequenceId/'
                to='/marketing/sms/drip-sequences/:dripSequenceId/details'
            />
            <PageRoute
                // @ts-expect-error
                exact={true}
                path='/marketing/sms/drip-sequences/:dripSequenceId/:tab'
                reportKey='smsDripEditionEngagement'
                initialFilterKey='sentSmsDripSequences'
                idParam='dripSequenceId'
                component={
                    NutClientConfig.hasSmsCampaignsFeature
                        ? SMSDripSequencePage
                        : SmsCampaignsFeatureGate
                }
            />
            <RestrictedRoute
                path='/marketing/sms-setup/:id?'
                exact={true}
                render={(renderProps) =>
                    NutClientConfig.hasSmsCampaignsFeature ? (
                        <SmsSettingsPageStandalone {...renderProps} />
                    ) : (
                        <SmsCampaignsFeatureGate />
                    )
                }
                hasAccess={
                    canAccessSetup &&
                    props.hasFullAccessToCampaigns &&
                    NutClientConfig.hasSMSCampaigns
                }
                redirectPath={props.redirectPath}
            />
        </Switch>
    );
};
